import React, { useMemo, FC } from 'react';
import { useDispatch } from 'react-redux';
import {
  Table,
  useTable,
  Flex,
  ButtonV2 as Button,
  Badge,
} from 'portal-commons';
import { faFileLock } from '@fortawesome/pro-regular-svg-icons';

import {
  brandController,
  BrandSenderId,
  BrandLOA,
  BrandStatus,
  BrandDetail,
  BrandTrademark,
} from '@/apis/core';
import { humanize } from '@/utils/string';
import { convertTimeWithTimezone } from '@/utils/time';
import { generateAndDownloadFile } from '@/utils/file';
import {
  CLEAR_LOADING_MESSAGE,
  SET_LOADING_MESSAGE,
} from '@/shared_elements/actions';
import { STATUS_BADGE_COLOR_MAP } from '@/constants/enum-mapping';
import { DetailsSection } from './components';

const SENDER_ID_HEADERS = [
  { id: 'senderId', label: 'Sender ID to allow', sortable: false },
  { id: 'associatedDcaList', label: 'Associated DCAs', sortable: false },
  {
    id: 'protectedSenderIdList',
    label: 'SENDER ID(S) TO BLOCK',
    sortable: false,
  },
  {
    id: 'trademark',
    label: 'Trademark',
    sortable: false,
  },
];

const LOA_HEADERS = [
  { id: 'fileName', label: 'Document', sortable: false },
  { id: 'status', label: 'Status', sortable: false },
  { id: 'completionDate', label: 'Completion Date', sortable: false },
];

const SenderIdTab: FC<{ brand: BrandDetail; trademarks: BrandTrademark[] }> = ({
  brand,
  trademarks,
}) => {
  const dispatch = useDispatch();
  const { uid: brandUid, status } = brand;
  const isDeleted = status === BrandStatus.Deleted;

  const downloadLoaDoc = async (docUid: string) => {
    dispatch({ type: SET_LOADING_MESSAGE, payload: 'Downloading LOA' });
    try {
      const response = await brandController.getLoaDoc(brandUid, docUid, {
        format: 'pdf',
      });
      if (response) {
        const { data, filename } = response;
        const file = new File([data as ArrayBuffer], filename, {
          type: 'application/pdf',
        });
        generateAndDownloadFile(file);
      }
    } finally {
      dispatch({ type: CLEAR_LOADING_MESSAGE });
    }
  };

  const senderIdTable = useTable<BrandSenderId>(
    async () => {
      const records =
        !isDeleted && brandUid
          ? (await brandController.getSenderIDs(brandUid)) || []
          : [];
      return {
        data: records,
        total: records.length,
        page: 1,
        rowsPerPage: Math.min(records.length, 10),
      };
    },
    { deps: [brandUid], inMemory: true }
  );

  const loaTable = useTable<BrandLOA>(
    async () => {
      const records = brandUid
        ? (await brandController.getLOAs(brandUid)) || []
        : [];
      return {
        data: records,
        total: records.length,
        page: 1,
        rowsPerPage: Math.min(records.length, 10),
      };
    },
    { deps: [brandUid], inMemory: true }
  );

  const senderIdDisplayData = useMemo(() => {
    const renderTrademarkCol = (s: BrandSenderId) => {
      if (!s.trademarkUid) {
        return <Badge type="info">Pending Trademark Assignment</Badge>;
      }
      const t = trademarks.find((tm) => tm.uid === s.trademarkUid);
      return (
        <div>
          {t?.trademarkText || s.trademarkUid}
          {t?.outcome?.toLowerCase() === 'expired' && (
            <Badge type="error" sx={{ ml: '8px' }}>
              Expired
            </Badge>
          )}
        </div>
      );
    };
    return senderIdTable.tableData.map((d) => ({
      senderId: <span data-testid="senderId">{d.senderId || 'N/A'}</span>,
      associatedDcaList: (
        <span data-testid="associatedDcaList">
          {d.associatedDcaList || 'N/A'}
        </span>
      ),
      protectedSenderIdList: (
        <span data-testid="protectedSenderIdList">
          {d.protectedSenderIdList || 'N/A'}
        </span>
      ),
      trademark: renderTrademarkCol(d),
    }));
  }, [senderIdTable.tableData, trademarks]);

  const loaDisplayData = useMemo(() => {
    return [...loaTable.tableData]
      .sort((a, b) => b.signDate - a.signDate)
      .map((d) => ({
        fileName:
          d.status === 'COMPLETED' ? (
            <Button
              shape="square"
              variant="fill"
              color="primary"
              size="sm"
              onClick={() => downloadLoaDoc(d.docUid)}
              data-testid="downloadLoaButton"
            >
              Download LOA
            </Button>
          ) : (
            <span data-testid="fileName">N/A</span>
          ),
        status: d.status ? (
          <Badge
            size="md"
            shape="rectangle"
            variant="fill"
            type={STATUS_BADGE_COLOR_MAP[d.status] || 'neutral'}
            data-testid="status"
          >
            {humanize(d.status)}
          </Badge>
        ) : (
          <span data-testid="status">N/A</span>
        ),
        completionDate: (
          <span data-testid="completionDate">
            {convertTimeWithTimezone(d.signDate, undefined, 'MM/DD/YYYY') ||
              'N/A'}
          </span>
        ),
      }));
  }, [loaTable.tableData]);

  return (
    <Flex sx={{ flexDirection: 'column', gap: '24px' }}>
      <Table
        emptyBlockHeight="180px"
        loading={senderIdTable.loading}
        headRows={SENDER_ID_HEADERS}
        emptyState="no sender IDs to view"
        disableHover={true}
        data={senderIdDisplayData}
        data-testid="senderIdTable"
      />
      <DetailsSection
        title="Letter of Authorization"
        icon={faFileLock}
        data-testid="loaSection"
      >
        <Table
          emptyBlockHeight="180px"
          loading={loaTable.loading}
          headRows={LOA_HEADERS}
          emptyState="no LOAs to view"
          disableHover={true}
          data={loaDisplayData}
          data-testid="loaTable"
        />
      </DetailsSection>
    </Flex>
  );
};

export default SenderIdTab;
